import { initializeApp } from "firebase/app";
import {
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  multiFactor,
  TotpMultiFactorGenerator,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectDatabaseEmulator, getDatabase } from "firebase/database";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

/**
 * Keep authDomain on the Firebase-provisioned handler domain until the Google
 * OAuth client also lists custom-host redirect URIs (otherwise: redirect_uri_mismatch).
 *
 * After adding these Authorized redirect URIs + JS origins in Google Cloud Console
 * (APIs & Services → Credentials → Web client):
 *   https://portal.fix123.ca/__/auth/handler
 *   https://ets-rd-portal.web.app/__/auth/handler
 *   https://portal.fix123.ca
 *   https://ets-rd-portal.web.app
 * you can set VITE_FIREBASE_AUTH_DOMAIN to the portal host for same-origin redirect SSO.
 * @see https://firebase.google.com/docs/auth/web/redirect-best-practices
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "demo-api-key",
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "ets-remote-desktop.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "demo-srd",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? "demo-srd.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "0",
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? "1:0:web:demo",
  databaseURL:
    import.meta.env.VITE_FIREBASE_DATABASE_URL ??
    "https://demo-srd-default-rtdb.firebaseio.com",
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const rtdb = getDatabase(app);
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });
googleProvider.addScope("email");
googleProvider.addScope("profile");

const useEmulators = import.meta.env.VITE_USE_EMULATORS === "true";
const emulatorHost = import.meta.env.VITE_FIREBASE_EMULATOR_HOST ?? "127.0.0.1";

if (useEmulators) {
  connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, emulatorHost, 8080);
  connectFunctionsEmulator(functions, emulatorHost, 5001);
  connectDatabaseEmulator(rtdb, emulatorHost, 9000);
}

const siteKey = import.meta.env.VITE_FIREBASE_APP_CHECK_SITE_KEY;
if (siteKey && siteKey !== "REPLACE_ME" && !useEmulators) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

/** MFA enrollment placeholder — wire full TOTP UI in hardening pass. */
export async function startTotpMfaEnrollmentPlaceholder(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Sign in before enrolling MFA.");
  }
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return secret.generateQrCodeUrl(user.email ?? "support", "SRD Support");
}
